// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { apiFetch, request } from '@/lib/api'
import type { PrepNote } from '@/types'

// Intake is a property of an ENCOUNTER — every action carries the appointmentId.
export interface IntakeTarget {
  patientId: string
  appointmentId?: string
  language: string
  phone?: string // optional SMS delivery-target override; delivery only, never identity
}

// Post-send feedback surfaced identically on the worklist + the encounter card.
export interface IntentResult {
  emailSent: boolean
  deeplinkToken: string
  expiresAt: string
}

// POST /v1/intents — remote self-eval link for one encounter.
export async function sendSelfEvalIntent(target: IntakeTarget): Promise<IntentResult> {
  const res = await apiFetch('/v1/intents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(target),
  })
  if (!res.ok) throw new Error(`Server returned ${res.status}`)
  const data = (await res.json().catch(() => ({}))) as { emailSent?: boolean; deeplinkToken?: string; expiresAt?: string }
  return { emailSent: data.emailSent !== false, deeplinkToken: data.deeplinkToken ?? '', expiresAt: data.expiresAt ?? '' }
}

// One prior, redone-and-retained intake for an encounter (status='superseded').
export interface SupersededAttempt {
  interactionId: string
  reason?: string
  startedAt: string
  supersededAt: string
}

// GET /v1/appointments/{appointmentId}/superseded-intakes — redo history for an encounter.
export async function listSupersededAttempts(appointmentId: string): Promise<SupersededAttempt[]> {
  const res = await apiFetch(`/v1/appointments/${encodeURIComponent(appointmentId)}/superseded-intakes`)
  if (!res.ok) throw new Error(`Server returned ${res.status}`)
  const data = (await res.json().catch(() => ({}))) as { attempts?: SupersededAttempt[] }
  return data.attempts ?? []
}

// POST /v1/interactions/{interactionId}/cancel result — the cancelled intake.
export interface CancelIntakeResult {
  status: string
}

// POST /v1/interactions/{interactionId}/cancel — abandon a stalled intake; the
// appointment reverts to not_sent server-side so Send/Start reappear.
export function cancelIntake(interactionId: string): Promise<CancelIntakeResult> {
  return request<CancelIntakeResult>(`/v1/interactions/${encodeURIComponent(interactionId)}/cancel`, {
    method: 'POST',
  })
}

// POST /v1/interactions/{interactionId}/supersede — redo a COMPLETED intake. The old
// interaction is retained as history but superseded; the appointment reverts to not_sent
// server-side so Send/Start reappear for a fresh attempt. `reason` is optional.
export function supersedeIntake(interactionId: string, reason?: string): Promise<CancelIntakeResult> {
  return request<CancelIntakeResult>(`/v1/interactions/${encodeURIComponent(interactionId)}/supersede`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? '' }),
  })
}

export interface ResetAppointmentIntakeResult {
  interactionId: string
  action: 'cancelled' | 'superseded' | 'none'
  interactionStatus: string
  intakeStatus: 'not_sent'
}

// POST /v1/appointments/{appointmentId}/reset-intake — tester/staff recovery action
// for one encounter. The API chooses cancel vs supersede based on the latest attempt.
export function resetAppointmentIntake(appointmentId: string, reason?: string): Promise<ResetAppointmentIntakeResult> {
  return request<ResetAppointmentIntakeResult>(`/v1/appointments/${encodeURIComponent(appointmentId)}/reset-intake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? '' }),
  })
}

// One-time kiosk lock token from POST /v1/kiosk/start (in-clinic intake).
export interface StartKioskResponse {
  lockToken: string
  expiresAt: string
}

// POST /v1/kiosk/start — mint the single-use kiosk lock token for the tablet hand-off.
export function startKiosk(target: IntakeTarget): Promise<StartKioskResponse> {
  return request<StartKioskResponse>('/v1/kiosk/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(target),
  })
}

// patient-web kiosk URL carrying the lock token — never PHI / the patient id.
export function buildKioskUrl(patientWebUrl: string, lockToken: string, returnUrl: string): string {
  return `${patientWebUrl.replace(/\/$/, '')}/kiosk?kt=${encodeURIComponent(lockToken)}&return=${encodeURIComponent(returnUrl)}`
}

// POST /v1/patients/{id}/prep-note/regenerate — rebuild this patient's prep note from
// the intake transcript. Returns the fresh note (status submitted for kiosk, draft for
// remote). 404 = no completed intake yet; 503 = generator unavailable.
export function regeneratePrepNote(patientId: string): Promise<PrepNote> {
  return request<PrepNote>(`/v1/patients/${encodeURIComponent(patientId)}/prep-note/regenerate`, {
    method: 'POST',
  })
}

// One turn of the intake conversation, in order. System lines are muted context markers.
export interface TranscriptMessage {
  role: 'patient' | 'assistant' | 'system'
  content: string
  createdAt: string
}

export interface Transcript {
  conversationId: string
  messages: TranscriptMessage[]
}

// GET /v1/patients/{id}/transcript[?interactionId=] — the ordered intake chat log for
// this patient (scoped to one encounter's interaction when interactionId is passed).
export function getTranscript(patientId: string, interactionId?: string): Promise<Transcript> {
  const qs = interactionId ? `?interactionId=${encodeURIComponent(interactionId)}` : ''
  return request<Transcript>(`/v1/patients/${encodeURIComponent(patientId)}/transcript${qs}`)
}

// Normalize the UI language to the two-letter intent language ('en' | 'es').
export function intentLang(language?: string): 'en' | 'es' {
  return language?.startsWith('es') ? 'es' : 'en'
}
